import { WORD_COUNT_OPTIONS, type WordCount } from "../../shared/protocol";
import type { DuelState } from "../lib/useDuel";
import { Waiting } from "../components/Spinner";

type Props = {
  name: string;
  onName: (v: string) => void;
  wordCount: WordCount;
  onWordCount: (n: WordCount) => void;
  joinCode: string;
  onJoinCode: (v: string) => void;
  duel: DuelState;
  onCreate: () => void;
  onCancel: () => void;
  onJoin: () => void;
  onBack: () => void;
};

export function Lobby({
  name,
  onName,
  wordCount,
  onWordCount,
  joinCode,
  onJoinCode,
  duel,
  onCreate,
  onCancel,
  onJoin,
  onBack,
}: Props) {
  const hosting = duel.side === "host" && (duel.phase === "creating" || duel.phase === "waiting");
  const joining = duel.side === "guest" && (duel.phase === "joining" || duel.phase === "waiting");

  return (
    <div className="stack">
      <div className="stack-tight">
        <h1 className="title">Battle</h1>
        <p className="subtitle">
          {wordCount} words, the same for both players — best score wins. You each read them in
          your own language and answer in English, so a French and a Korean player can duel on the
          same cards.
        </p>
      </div>

      <label className="field">
        Your name
        <input
          className="input"
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder="Player"
          maxLength={16}
          autoComplete="nickname"
        />
      </label>

      <div className="duo">
        <div className="mini-card">
          <div className="mini-card-title">Create a duel</div>

          {hosting ? (
            <>
              {duel.code ? (
                <div className="room-code" aria-label={`Room code ${duel.code.split("").join(" ")}`}>
                  {duel.code}
                </div>
              ) : null}
              <Waiting centered>
                {duel.code ? "Waiting for opponent…" : "Creating…"}
              </Waiting>
              <button type="button" className="btn-link" onClick={onCancel}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <div className="helper">You&rsquo;ll get a 4-letter code to share</div>
              <div className="stack-tight">
                <div className="field-label">Words</div>
                <div className="segmented" role="group" aria-label="Number of words">
                  {WORD_COUNT_OPTIONS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      aria-pressed={n === wordCount}
                      onClick={() => onWordCount(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" className="btn btn-primary" onClick={onCreate}>
                Create room
              </button>
            </>
          )}
        </div>

        <div className="mini-card">
          <div className="mini-card-title">Join a duel</div>
          <div className="helper">Enter the code your opponent shared</div>
          <input
            className="input code-input"
            value={joinCode}
            onChange={(e) => onJoinCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onJoin();
            }}
            placeholder="CODE"
            aria-label="Room code"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
          />
          {joining ? (
            <Waiting>{duel.phase === "waiting" ? "Waiting for the host…" : "Connecting…"}</Waiting>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={onJoin}>
              Join
            </button>
          )}
          {duel.error && <div className="error">{duel.error}</div>}
        </div>
      </div>

      <div>
        <button type="button" className="btn-link btn-back" onClick={onBack}>
          ← Back to practice
        </button>
      </div>
    </div>
  );
}
